import type { ExerciseDefinition, GeneratedQuestion, GradeResult } from '../exercises/types.ts';
import {
  SKILL_RHYTHM_COMPOUND,
  SKILL_RHYTHM_DIVISIONS,
  SKILL_RHYTHM_DOTTED,
  SKILL_RHYTHM_RESTS,
  SKILL_RHYTHM_SIMPLE,
  SKILL_RHYTHM_SUBDIVISIONS,
  SKILL_RHYTHM_TIES,
  SKILL_RHYTHM_TRIPLETS,
} from '../skills.ts';
import { describeRhythmConfig, validateRhythmConfig, type CountUnit, type RhythmConfig, type RhythmMode } from './config.ts';
import { engravePhrase, tieContinuationTicks } from './engrave.ts';
import { phraseFeatures } from './features.ts';
import { generateRhythmPhrases } from './generate.ts';
import { gradeTaps, rhythmFeedback, rhythmGradeCode, type RhythmGrade, type RhythmGradeCode } from './grade.ts';
import { getTimeSignature, resolveGrouping } from './meter.ts';
import { expectedTapTicks, phraseMeter, type RhythmPhrase } from './phrase.ts';

/**
 * Rhythm tapping as a registered exercise family. A question is one phrase at
 * the configured tempo; an answer is the list of tap times the learner played
 * (ms from the phrase's first beat, already corrected for device latency).
 *
 * The stored `expected` carries the tap and tied-note positions derived from
 * the phrase by the engraver, so the SQL grader (`app_private.grade_rhythm`)
 * regrades from the same authoritative data without re-implementing notation.
 */

export const RHYTHM = 'rhythm' as const;

export interface RhythmPrompt {
  phrase: RhythmPhrase;
  /** Beats per minute in the meter's beat (dotted quarter in compound meters, even when counting eighths). */
  tempo: number;
  /** The beat grouping (accents, labels), for meters with more than one. */
  grouping?: number[];
  /** Compound meters counted in eighths: the metronome clicks eighths and the tempo reads "♪ = 3 × tempo". */
  countUnit?: CountUnit;
  /** Strong-beats practice: tap only the strong beats; nothing is notated. */
  mode?: RhythmMode;
}

/** The prompt for a phrase under a config (optional fields only when they apply). */
export function rhythmPrompt(phrase: RhythmPhrase, config: RhythmConfig): RhythmPrompt {
  const ts = getTimeSignature(config.timeSignature);
  return {
    phrase,
    tempo: config.tempo,
    ...(ts.groupings.length > 1 || config.grouping ? { grouping: resolveGrouping(ts, config.grouping) } : {}),
    ...(config.countUnit === 'eighth' ? { countUnit: 'eighth' as const } : {}),
    ...(config.mode === 'strong-beats' ? { mode: 'strong-beats' as const } : {}),
  };
}

export interface RhythmExpected extends RhythmPrompt {
  /** Ticks where a tap is expected (every sound's start). */
  tapTicks: number[];
  /** Ticks of written tied continuations (tapping there is a "tapped a tie" error). */
  tieTicks: number[];
}

export interface RhythmAnswer {
  taps: number[];
}

/** Bounds on a submitted answer; the SQL grader enforces the same. */
export const RHYTHM_ANSWER_LIMITS = { maxTaps: 200, maxAbsMs: 600_000 } as const;

export function rhythmExpected(phrase: RhythmPhrase, tempo: number, extra: Omit<RhythmPrompt, 'phrase' | 'tempo'> = {}): RhythmExpected {
  return { phrase, tempo, ...extra, tapTicks: expectedTapTicks(phrase), tieTicks: tieContinuationTicks(engravePhrase(phrase)) };
}

/** Taps must be whole milliseconds within sane bounds. Returns null for anything else. */
export function parseRhythmAnswer(input: unknown): RhythmAnswer | null {
  if (typeof input !== 'object' || input === null || !Array.isArray((input as { taps?: unknown }).taps)) return null;
  const taps = (input as { taps: unknown[] }).taps;
  if (taps.length > RHYTHM_ANSWER_LIMITS.maxTaps) return null;
  if (!taps.every((t) => typeof t === 'number' && Number.isInteger(t) && Math.abs(t) <= RHYTHM_ANSWER_LIMITS.maxAbsMs)) return null;
  return { taps: taps as number[] };
}

/** Skills a phrase practises: its meter's pulse plus each feature it actually contains. */
export function rhythmSkillsForPhrase(phrase: RhythmPhrase): string[] {
  const f = phraseFeatures(phrase);
  const skills = [phraseMeter(phrase).kind === 'compound' ? SKILL_RHYTHM_COMPOUND : SKILL_RHYTHM_SIMPLE];
  if (f.divisions) skills.push(SKILL_RHYTHM_DIVISIONS);
  if (f.subdivisions) skills.push(SKILL_RHYTHM_SUBDIVISIONS);
  if (f.dotted) skills.push(SKILL_RHYTHM_DOTTED);
  if (f.rests) skills.push(SKILL_RHYTHM_RESTS);
  if (f.ties) skills.push(SKILL_RHYTHM_TIES);
  if (f.triplets) skills.push(SKILL_RHYTHM_TRIPLETS);
  return skills;
}

export function generateRhythmQuestions(config: RhythmConfig, seed: string): GeneratedQuestion<RhythmPrompt, RhythmExpected>[] {
  return generateRhythmPhrases(config, seed).map((phrase, position) => {
    const { phrase: _phrase, tempo: _tempo, ...extra } = rhythmPrompt(phrase, config);
    return {
      position,
      prompt: rhythmPrompt(phrase, config),
      expected: rhythmExpected(phrase, config.tempo, extra),
      // Strong beats practise the pulse only.
      skillIds: config.mode === 'strong-beats' ? rhythmSkillsForPhrase(phrase).slice(0, 1) : rhythmSkillsForPhrase(phrase),
    };
  });
}

export interface RhythmGradeResult extends GradeResult<RhythmGradeCode> {
  grade: RhythmGrade;
}

export function gradeRhythmAnswer(expected: RhythmExpected, answer: RhythmAnswer): RhythmGradeResult {
  const grade = gradeTaps(expected.phrase, expected.tempo, answer.taps);
  return { correct: grade.correct, code: rhythmGradeCode(grade), grade };
}

export const rhythmTapping: ExerciseDefinition<RhythmConfig, RhythmPrompt, RhythmExpected, RhythmAnswer, RhythmGradeCode> = {
  type: RHYTHM,
  // v2: switched-on options are required in 2 of every 3 questions. v3: triplets.
  // v4: beat groupings shape the rhythm, 7/4, counting compound meters in eighths, strong-beats practice.
  engineVersion: 4,
  label: 'Rhythm',
  // Listening to the demonstration is part of learning the rhythm, not a hint.
  maxHintLevel: 0,
  validateConfig: validateRhythmConfig,
  generate: generateRhythmQuestions,
  grade: (expected, answer) => {
    const { correct, code } = gradeRhythmAnswer(expected, answer);
    return { correct, code };
  },
  parseAnswer: (input) => parseRhythmAnswer(input),
  hint: () => null,
  feedback: (_prompt, expected, answer) => rhythmFeedback(gradeTaps(expected.phrase, expected.tempo, answer.taps)),
  describeConfig: describeRhythmConfig,
  skillsFor(config) {
    const ts = getTimeSignature(config.timeSignature);
    const skills = [ts.kind === 'compound' ? SKILL_RHYTHM_COMPOUND : SKILL_RHYTHM_SIMPLE];
    if (config.mode === 'strong-beats') return skills;
    const beatValue = ts.kind === 'compound' || ts.denominator === 4 ? 'quarter' : 'half';
    const shorter = config.noteValues.filter((v) => ['half', 'quarter', 'eighth', 'sixteenth'].indexOf(v) > ['half', 'quarter', 'eighth', 'sixteenth'].indexOf(beatValue));
    if (shorter.length > 0) skills.push(SKILL_RHYTHM_DIVISIONS);
    if (config.noteValues.includes('sixteenth') || (ts.denominator === 2 && config.noteValues.includes('eighth')) || config.dotted) skills.push(SKILL_RHYTHM_SUBDIVISIONS);
    if (config.dotted) skills.push(SKILL_RHYTHM_DOTTED);
    if (config.rests) skills.push(SKILL_RHYTHM_RESTS);
    if (config.ties) skills.push(SKILL_RHYTHM_TIES);
    if (config.triplets) skills.push(SKILL_RHYTHM_TRIPLETS);
    return skills;
  },
};
